import { paramCase, snakeCase } from 'change-case';
import castArray from 'lodash/castArray.js';
// Ensure lodash is installed
import get from 'lodash/get.js';
import identity from 'lodash/identity.js';
import map from 'lodash/map.js';
import pickBy from 'lodash/pickBy.js';
import pluralize from 'pluralize';

import axios from '@/plugins/axios.js';
import { promiseError, toggleLoading } from '@/store/utilities/Helpers.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default {
    collection: model => {
        return {
            getters: {
                pagination: state => ({
                    page: get(state, 'pagination.page'),
                    per_page: get(state, 'pagination.perPage'),
                }),

                queryBuilderSorting(state) {
                    const sorts = pickBy(state.sort, identity);

                    return Object.keys(sorts).map(key => {
                        return sorts[key].toLowerCase() === 'desc' ? `-${key}` : key;
                    });
                },
            },

            actions: {
                fetch(buildQuery, { withMenu = true } = {}) {
                    return async (context, payload) => {
                        const { dispatch } = context;

                        toggleLoading(context);

                        const queryBuilder = new QueryBuilder(paramCase(pluralize(model)));

                        // List pages that do not need course navigation should pass withMenu: false
                        // and load menu via MenuService.loadAndSetCourseMenu when appropriate.
                        if (withMenu) {
                            console.warn(
                                `EdverumTable.fetch() for model '${model}' uses deprecated menu loading. Consider using MenuService.loadAndSetCourseMenu() instead.`
                            );
                            queryBuilder.menu();
                        }

                        if (buildQuery) {
                            buildQuery({ context, queryBuilder, payload });
                        }

                        try {
                            const { data } = await axios.get(queryBuilder.toUrl());

                            return Promise.all([
                                dispatch('updatePageMeta', data.meta),
                                dispatch('hydrate', data.data),
                            ]).then(() => data.data);
                        } finally {
                            toggleLoading(context);
                        }
                    };
                },

                fetchOne(buildQuery, afterLoad) {
                    return async (context, modelId) => {
                        const { dispatch } = context;

                        toggleLoading(context);

                        const queryBuilder = new QueryBuilder(`${paramCase(pluralize(model))}/${modelId}`);

                        if (buildQuery) {
                            buildQuery({ context, queryBuilder });
                        }

                        try {
                            // Add deprecated warning for generic EdverumTable menu usage
                            console.warn(
                                `EdverumTable.fetchOne() for model '${model}' uses deprecated menu loading. Consider using MenuService.loadAndSetCourseMenu() instead.`
                            );
                            const { data } = await axios.get(queryBuilder.menu().toUrl());

                            toggleLoading(context);

                            await dispatch('add', data.data);

                            if (afterLoad) {
                                afterLoad({
                                    data: data.data,
                                    meta: data.meta,
                                    payload: modelId,
                                    context,
                                });
                            }

                            return data.data;
                        } catch (error) {
                            console.error(error);

                            toggleLoading(context);

                            promiseError({
                                key: `${snakeCase(model)}_load_error`,
                                context,
                            });

                            return error;
                        }
                    };
                },

                addModel(buildQuery = null, errorHandle = false) {
                    return async (context, subject) => {
                        const queryBuilder = new QueryBuilder(paramCase(pluralize(model)));

                        if (buildQuery) {
                            buildQuery({
                                context,
                                queryBuilder,
                                payload: subject,
                            });
                        }

                        const errorHandler = errorHandle ? { errorHandle: false } : {};

                        const { data } = await axios.post(queryBuilder.toUrl(), subject, errorHandler);

                        castArray(data.data).forEach(addedModel => context.dispatch('add', addedModel));

                        return data.data;
                    };
                },

                updateModel(buildQuery, updater = null, errorHandle = false) {
                    return async (context, payload) => {
                        const { commit } = context;

                        const queryBuilder = new QueryBuilder(`${paramCase(pluralize(model))}/${get(payload, 'id')}`);

                        if (buildQuery) {
                            buildQuery({ context, queryBuilder, payload });
                        }

                        const errorHandler = errorHandle ? { errorHandle: false } : {};

                        try {
                            const { data } = await axios.put(queryBuilder.toUrl(), payload, errorHandler);

                            if (!updater) {
                                commit('UPDATE_MODEL', data.data);
                            } else {
                                updater({ context, data: data.data, payload });
                            }

                            return data.data;
                        } catch (error) {
                            promiseError({
                                key: `${snakeCase(model)}_update_error`,
                                context,
                            });

                            return error;
                        }
                    };
                },

                deleteModel(buildQuery, errorHandle = false) {
                    return async (context, payload) => {
                        const { commit } = context;

                        const queryBuilder = new QueryBuilder(`${paramCase(pluralize(model))}/${get(payload, 'id')}`);

                        if (buildQuery) {
                            buildQuery({ context, queryBuilder, payload });
                        }

                        const errorHandler = errorHandle ? { errorHandle: false } : {};

                        try {
                            const { data } = await axios.delete(queryBuilder.toUrl(), errorHandler);

                            commit('DELETE_MODEL', payload.id);

                            return data.data;
                        } catch (error) {
                            promiseError({
                                key: `${snakeCase(model)}_delete_error`,
                                context,
                            });

                            return error;
                        }
                    };
                },

                fetchAssignableGroups() {
                    return async context => {
                        const queryBuilder = new QueryBuilder('groups');

                        // Filter prefetches are best-effort: errorHandle: false keeps
                        // AccessDeniedInterceptor from redirecting the page to
                        // /restricted, and a failure degrades to an empty filter.
                        try {
                            const { data } = await axios.get(queryBuilder.toUrl(), { errorHandle: false });

                            context.commit('SET_ASSIGNABLE_GROUPS', data.data);

                            return data.data;
                        } catch (error) {
                            console.error(error);

                            context.commit('SET_ASSIGNABLE_GROUPS', []);

                            return [];
                        }
                    };
                },

                fetchAssignableCourseCodes() {
                    return async context => {
                        const queryBuilder = new QueryBuilder('course-codes');

                        try {
                            const { data } = await axios.get(queryBuilder.toUrl(), { errorHandle: false });

                            context.commit('SET_ASSIGNABLE_COURSE_CODES', data.data);

                            return data.data;
                        } catch (error) {
                            console.error(error);

                            context.commit('SET_ASSIGNABLE_COURSE_CODES', []);

                            return [];
                        }
                    };
                },

                updateModelTags() {
                    return async (context, { subject, tags, modelKey, tagType }) => {
                        const { commit } = context;

                        const key = modelKey ?? 'id';

                        try {
                            const { data } = await axios.put(
                                `/api/${paramCase(pluralize(model))}/${subject[key]}/tags?type=${snakeCase(model)}`,
                                {
                                    tags: tags.map(tag => (typeof tag === 'string' ? tag : get(tag, 'name.en'))),
                                    tagType,
                                },
                                { retry: false }
                            );

                            commit('UPDATE_TAGS', { subject, tags: data.tags });
                            return data.tags;
                        } catch (error) {
                            promiseError({
                                key: `${snakeCase(model)}_tags_update_error`,
                                context,
                            });

                            return error;
                        }
                    };
                },

                updateModelCourseCodes() {
                    return async (context, { subject, course_codes, modelKey }) => {
                        const { commit } = context;

                        const key = modelKey ?? 'id';

                        try {
                            const { data } = await axios.put(`/api/${paramCase(pluralize(model))}/${subject[key]}`, {
                                ...subject,
                                // eslint-disable-next-line camelcase
                                course_codes: course_codes.map(courseCode => get(courseCode, 'id')),
                            });

                            commit('UPDATE_COURSE_CODES', {
                                subject,
                                course_codes: data.course_codes,
                            });
                            return data.course_codes;
                        } catch (error) {
                            promiseError({
                                key: `${snakeCase(model)}_course_codes_update_error`,
                                context,
                            });

                            return error;
                        }
                    };
                },

                updateModelGroups() {
                    return async (context, { subject, groups, modelKey }) => {
                        const { commit } = context;

                        const key = modelKey ?? 'id';

                        try {
                            await axios.put(
                                `/api/${paramCase(pluralize(model))}/${subject[key]}/groups`,
                                {
                                    groups: map(groups, 'id'),
                                },
                                { retry: false }
                            );

                            commit('UPDATE_GROUPS', { subject, groups });

                            return groups;
                        } catch (error) {
                            promiseError({
                                key: `${snakeCase(model)}_groups_update_error`,
                                context,
                            });
                            return error;
                        }
                    };
                },

                updateNoteTags() {
                    return async (context, { subject, tags }) => {
                        const { commit, dispatch } = context;
                        try {
                            const { data } = await axios.put(
                                `/api/${paramCase(pluralize(model))}/${subject.id}/tags?type=${snakeCase(model)}-${
                                    subject.id
                                }`,
                                {
                                    tags: tags.map(tag => (typeof tag === 'string' ? tag : get(tag, 'name.en'))),
                                }
                            );

                            commit('UPDATE_TAGS', { subject, tags });
                            await dispatch('refreshTable');

                            return data.data;
                        } catch (error) {
                            promiseError({
                                key: `${snakeCase(model)}_tags_update_error`,
                                context,
                            });

                            return error;
                        }
                    };
                },

                removeChecked(payloadHandler) {
                    return async (context, collection) => {
                        const { dispatch } = context;

                        const defaultPayload = {
                            data: { ids: map(collection, 'id') },
                        };

                        const payload = payloadHandler ? payloadHandler(collection) : defaultPayload;

                        try {
                            const { data } = await axios.delete(`/api/${paramCase(pluralize(model))}`, payload);

                            collection.forEach(removeModel => {
                                dispatch('remove', removeModel.id);
                            });

                            return data.data;
                        } catch (error) {
                            promiseError({
                                key: `${snakeCase(model)}_delete_error`,
                                context,
                            });

                            return error;
                        }
                    };
                },

                clearSort({ commit }) {
                    commit('CLEAR_SORTING');
                },

                async paginateAndFetch({ dispatch }, paginate) {
                    await dispatch('updatePageParams', paginate);
                    await dispatch('purge');
                    await dispatch('fetch');
                },

                reset(collection) {
                    return ({ commit, dispatch }) => {
                        dispatch(`${collection}/clearSort`, null, {
                            root: true,
                        });

                        const url = new URL(window.location.href);
                        if (!url.searchParams.get('search')) {
                            commit('SET_SEARCH', null);
                        }
                    };
                },
            },

            mutations: {
                CLEAR_SORTING(state) {
                    Object.keys(state.sort).forEach(key => (state.sort[key] = null));
                },

                ADD_MODEL(state, subject) {
                    subject.order = state.length;

                    state[subject.id] = subject;
                },

                UPDATE_MODEL(state, subject) {
                    state[subject.id] = {
                        ...subject,
                        order: get(state[subject.id], 'order'),
                    };
                },

                DELETE_MODEL(state, subjectId) {
                    delete state[subjectId];
                },

                UPDATE_TAGS(state, { subject, tags }) {
                    subject.tags = tags;

                    state[subject.id] = subject;
                },

                UPDATE_GROUPS(state, { subject, groups }) {
                    subject.groups = groups;

                    state[subject.id] = subject;
                },

                UPDATE_COURSE_CODES(state, { subject, courseCodes }) {
                    subject.course_codes = courseCodes;

                    state[subject.id] = subject;
                },

                SET_ASSIGNABLE_GROUPS(state, groups) {
                    state.assignableGroups = groups;
                },

                SET_ASSIGNABLE_COURSE_CODES(state, courseCodes) {
                    state.assignableCourseCodes = courseCodes;
                },
            },
        };
    },

    search: {
        state: {
            search: null,
            filters: [],
        },

        getters: {
            hasQuery(state) {
                return state.search && state.search.length;
            },

            searchQuery(state) {
                return state.search;
            },

            filters(state) {
                return state.filters;
            },
        },

        mutations: {
            SET_SEARCH(state, query) {
                state.search = query;
            },

            SET_FILTERS(state, query) {
                state.filters = query;
            },

            RESET_FILTERS(state) {
                state.filters = [];
            },

            CLEAR_SEARCH(state) {
                state.search = null;
            },

            RESET_COURSE_CODES(state) {
                state.searchCourseCode = null;
            },
        },
    },

    searchTag: {
        state: {
            searchTag: null,
            assignableTags: [],
            allTags: [],
        },

        getters: {
            hasTagSearch(state) {
                return state.searchTag && state.searchTag.length;
            },

            searchTag(state) {
                return state.searchTag;
            },
        },

        actions: {
            fetchAssignableTags(endpoint) {
                return async context => {
                    const response = await axios.get(endpoint);

                    if (response) context.commit('SET_ASSIGNABLE_TAGS', response.data.data);
                };
            },
            fetchAllTags(endpoint) {
                return async context => {
                    const response = await axios.get(endpoint);

                    if (response) context.commit('SET_ALL_TAGS', response.data.data);
                };
            },
        },

        mutations: {
            SET_SEARCH_TAG(state, tag) {
                state.searchTag = tag;
            },

            SET_ASSIGNABLE_TAGS(state, tags) {
                state.assignableTags = tags;
            },

            SET_ALL_TAGS(state, tags) {
                state.allTags = tags;
            },
        },
    },

    searchGroup: {
        state: {
            searchGroup: null,
        },

        getters: {
            hasGroupSearch(state) {
                return state.searchGroup && state.searchGroup.toString().length;
            },

            searchGroup(state) {
                return state.searchGroup;
            },
        },

        actions: {},

        mutations: {
            SET_SEARCH_GROUP(state, group) {
                state.searchGroup = group;
            },
        },
    },

    searchCourseCode: {
        state: {
            searchCourseCode: null,
        },

        getters: {
            hasCourseCodeSearch(state) {
                return !!state.searchCourseCode;
            },

            searchCourseCode(state) {
                return state.searchCourseCode;
            },
        },

        mutations: {
            SET_SEARCH_COURSE_CODE(state, courseCode) {
                state.searchCourseCode = courseCode;
            },
        },
    },

    searchCourseType: {
        state: {
            searchCourseType: null,
        },

        getters: {
            hasCourseTypeSearch(state) {
                return !!state.searchCourseType;
            },

            searchCourseType(state) {
                return state.searchCourseType;
            },
        },

        mutations: {
            SET_SEARCH_COURSE_TYPE(state, courseType) {
                state.searchCourseType = courseType;
            },
        },
    },

    searchInstructor: {
        state: {
            searchInstructor: null,
        },

        getters: {
            hasInstructorSearch(state) {
                return !!state.searchInstructor;
            },

            searchInstructor(state) {
                return state.searchInstructor;
            },
        },

        mutations: {
            SET_SEARCH_INSTRUCTOR(state, instructor) {
                state.searchInstructor = instructor;
            },
        },
    },

    courseKeyEnabled: {
        state: {
            filterCourseKey: null,
        },

        getters: {
            hasCourseKeyFilter(state) {
                return !!state.filterCourseKey;
            },

            filterCourseKey(state) {
                return state.filterCourseKey;
            },
        },

        mutations: {
            SET_FILTER_COURSE_KEY(state, filterCourseKey) {
                state.filterCourseKey = filterCourseKey;
            },
        },
    },

    inProgress: {
        state: {
            inProgress: null,
        },

        getters: {
            hasInProgress(state) {
                return !!state.inProgress;
            },

            inProgress(state) {
                return state.inProgress;
            },
        },

        mutations: {
            SET_FILTER_IN_PROGRESS(state, filterInProgress) {
                state.inProgress = filterInProgress;
            },
        },
    },

    created: {
        state: {
            createdModels: [],
        },

        actions: {
            addCreatedModel({ commit }, model) {
                commit('ADD_CREATED_MODEL', model);
            },
        },

        mutations: {
            ADD_CREATED_MODEL(state, model) {
                state.createdModels.push(model);
            },
        },
    },

    viewArchived: {
        state: {
            viewArchived: false,
            type: null,
        },

        getters: {
            hasViewArchive(state) {
                return state.viewArchived;
            },

            searchViewArchive(state) {
                return state.viewArchived;
            },

            searchType(state) {
                return state.type;
            },
        },
        mutations: {
            TOGGLE_ARCHIVED_STATE(state, view) {
                state.viewArchived = view;
            },
        },
    },
};
